import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { keys, map } from 'libraries/lodash'
import Route from 'route-parser'
import Grid from '@mui/material/Grid'
import url from 'libraries/front-end-core/core/url'
import { URL_DOWNLOAD_DEAL_PDF, URL_DOWNLOAD_DEAL_ZIP, MAILTO, URL_DOWNLOAD_DEAL_EXCEL } from 'constants/urls'
import { MW_DEAL_DETAILS } from 'constants/controllers'
import { LayoutHeader, HeaderAction, HeaderActionGroup } from 'components/dealwatch/Layout'
import selectAolName from 'redux/selectors/selectAolName'
import { renderMarkdown, truncateMarkdown } from 'helpers/markdown'

const DealDetailsHeaderButtons = ({ deal, dealId, downloadUrl, onClickDownload }) => (
  <div className='flex items-center' data-testid='deal-detail-header-buttons'>
    <EmailIconButton {...{ deal, dealId }} />
    <PrintIconButton {...{ downloadUrl, dealId }} />
    <HeaderActionGroup>
      <DownloadPdfIconButton {...{ downloadUrl, dealId }} />
      <DownloadExcelFileIconButton {...{ onClickDownload, dealId, id: deal.id, title: deal.title }} />
      <DownloadZipFileIconButton {...{ downloadUrl, dealId }} />
    </HeaderActionGroup>
  </div>
)

const EmailIconButton = ({ deal, dealId }) => {
  const aol = useSelector(selectAolName)
  const dealUrl = `https://www.fromcounsel.com/${url(MW_DEAL_DETAILS, { aol, dealId })}`
  const mailParser = new Route(MAILTO)
  const subject = 'FromCounsel - shared content'
  const body = `A colleague of yours shared the FromCounsel Markets deal ${deal.id} ${deal.title} with you.\n\nPlease log in and use the following link to view this deal: ${dealUrl}`
  const email = 'mailto:' + mailParser.reverse({ subject, body })

  return (
    <HeaderAction
      icon='icon-email'
      description='Email details'
      href={email}
      target='_blank'
      rel='noopener noreferrer'
    />
  )
}

const PrintIconButton = ({ downloadUrl, dealId }) => (
  <HeaderAction
    icon='icon-printer'
    description='Print details'
    href={downloadUrl(URL_DOWNLOAD_DEAL_PDF, { dealId }, { d: 0 })}
    target='_blank'
    rel='noopener noreferrer'
  />
)

const DownloadPdfIconButton = ({ downloadUrl, dealId }) => (
  <HeaderAction
    icon='icon-file-pdf-box'
    description='Download details in PDF'
    href={downloadUrl(URL_DOWNLOAD_DEAL_PDF, { dealId }, { d: 1 })}
  />
)

const DownloadExcelFileIconButton = ({ onClickDownload, dealId, id, title }) => (
  <HeaderAction
    icon='icon-file-excel-box'
    description='Download details in Excel'
    onClick={() => onClickDownload(
      new Route(URL_DOWNLOAD_DEAL_EXCEL).reverse({ dealId }),
      `${id} - ${title}.xls`
    )}
  />
)

const DownloadZipFileIconButton = ({ downloadUrl, dealId }) => (
  <HeaderAction
    icon='icon-zip-box'
    description='Download deal documents in .zip file'
    href={downloadUrl(URL_DOWNLOAD_DEAL_ZIP, { dealId })}
  />
)

const DealDetailItem = ({ label, value, className = 'w12 mr8 mb2' }) => (
  <Grid container className='lh-title'>
    <Grid item className={className}>
      <span className='b'>{label}:</span>
    </Grid>
    <Grid item>
      <span>{value}</span>
    </Grid>
  </Grid>
)

export const ExpertCommentary = ({ value }) => {
  const [expand, setExpand] = useState(false)

  if (!value) {
    return null
  }

  const truncatedLength = 100
  const isLong = value.length > truncatedLength
  const displayText = !expand && isLong ? truncateMarkdown(value, truncatedLength) : value

  return (
    <DealDetailItem
      label='Expert commentary'
      className='min-w12 mr6'
      value={
        <>
          <div className='markdown-content' dangerouslySetInnerHTML={{ __html: renderMarkdown(displayText) }} />
          {!expand && isLong && (
            <p>
              <a href={null} className='pointer' onClick={() => setExpand(true)}>
                Read more
              </a>
            </p>
          )}
          {expand && (
            <p>
              <a href={null} className='pointer' onClick={() => setExpand(false)}>
                Show less
              </a>
            </p>
          )}
        </>
      }
    />
  )
}

const DealProperties = ({
  deal,
  dealType,
  dealId,
  downloadUrl,
  onClickDownload
}) => {
  const { expertCommentary, id, primaryDate, relatedEvents, title } = deal
  const aol = useSelector(selectAolName)

  return (
    <>
      <LayoutHeader title='Deal Details' spacing={0} cxNames='pv1 mb4'>
        <DealDetailsHeaderButtons {...{ deal, dealId, downloadUrl, onClickDownload }} />
      </LayoutHeader>
      <div className='ml6 f5'>
        <DealDetailItem label='Deal title' value={title} />
        <DealDetailItem label='FromCounsel ID' value={id} />
        <DealDetailItem label='Deal type' value={dealType} />
        <DealDetailItem label='Date' value={primaryDate.format('DD/MM/YYYY')} />
        <ExpertCommentary value={expertCommentary} />
        {!!keys(relatedEvents).length && (
          <DealDetailItem
            label='Related deals'
            value={map(relatedEvents, e => (
              <p key={e.uuid} className='mv0'>
                <a href={url(MW_DEAL_DETAILS, { aol, dealId: e.uuid })}>
                  {e.description}
                </a>
              </p>
            ))}
          />
        )}
      </div>
    </>
  )
}

export default DealProperties
